import * as THREE from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import Application from '../Application';
import EventEmitter from '../Utils/EventEmitter';
import { heroScreen } from './SceneAnchors';
/**
 * Pixel width of the iframe. The height is derived from the aspect ratio of the
 * screen it is projected onto, and the whole assembly is then scaled so those
 * pixels map exactly onto the screen glass in world space.
 */
const SCREEN_WIDTH_PX = 1280;
const IFRAME_PADDING = 32;
/**
 * How far the screen fades when viewed edge on. 0 disables the fade entirely
 * and leaves the site at full brightness from every angle.
 */
const DIM_FACTOR = 0.85;
export default class MonitorScreen extends EventEmitter {
    constructor() {
        super();
        this.application = new Application();
        this.scene = this.application.scene;
        this.cssScene = this.application.cssScene;
        this.sizes = this.application.sizes;
        this.resources = this.application.resources;
        this.camera = this.application.camera;
        this.videoTextures = {};
        this.mouseClickInProgress = false;
        this.shouldLeaveMonitor = false;
        this.position = new THREE.Vector3().copy(heroScreen.position);
        this.normal = new THREE.Vector3().copy(heroScreen.normal);
        // Match the pixel canvas to the proportions of the physical screen so
        // nothing rendered onto it is stretched.
        const screenAspect = heroScreen.height / heroScreen.width;
        this.screenSize = new THREE.Vector2(SCREEN_WIDTH_PX, Math.round(SCREEN_WIDTH_PX * screenAspect));
        this.iframeSize = new THREE.Vector2(this.screenSize.width - IFRAME_PADDING, this.screenSize.height - IFRAME_PADDING);
        this.createAnchors();
        // Create screen
        this.initializeScreenEvents();
        this.createIframe();
        const maxOffset = this.createTextureLayers();
        this.createEnclosingPlanes(maxOffset);
        this.createPerspectiveDimmer(maxOffset);
    }
    /**
     * Places one anchor in each scene at the screen's world transform, scaled so
     * that a `screenSize` sized plane laid out in local XY covers the glass
     * exactly. Every layer below is then positioned in local pixel space.
     */
    createAnchors() {
        const scale = heroScreen.width / this.screenSize.width;
        this.anchor = new THREE.Group();
        this.anchor.position.copy(heroScreen.position);
        this.anchor.quaternion.copy(heroScreen.quaternion);
        this.anchor.scale.setScalar(scale);
        this.scene.add(this.anchor);
        this.cssAnchor = new THREE.Group();
        this.cssAnchor.position.copy(heroScreen.position);
        this.cssAnchor.quaternion.copy(heroScreen.quaternion);
        this.cssAnchor.scale.setScalar(scale);
        this.cssScene.add(this.cssAnchor);
    }
    initializeScreenEvents() {
        document.addEventListener('mousemove', (event) => {
            // @ts-ignore
            const id = event.target.id;
            if (id === 'computer-screen') {
                // @ts-ignore
                event.inComputer = true;
            }
            // @ts-ignore
            this.inComputer = event.inComputer;
            if (this.inComputer && !this.prevInComputer) {
                this.camera.trigger('enterMonitor');
            }
            if (!this.inComputer &&
                this.prevInComputer &&
                !this.mouseClickInProgress) {
                this.camera.trigger('leftMonitor');
            }
            if (!this.inComputer &&
                this.mouseClickInProgress &&
                this.prevInComputer) {
                this.shouldLeaveMonitor = true;
            }
            else {
                this.shouldLeaveMonitor = false;
            }
            this.application.mouse.trigger('mousemove', [event]);
            this.prevInComputer = this.inComputer;
        }, false);
        document.addEventListener('mousedown', (event) => {
            // @ts-ignore
            this.inComputer = event.inComputer;
            this.application.mouse.trigger('mousedown', [event]);
            this.mouseClickInProgress = true;
            this.prevInComputer = this.inComputer;
        }, false);
        document.addEventListener('mouseup', (event) => {
            // @ts-ignore
            this.inComputer = event.inComputer;
            this.application.mouse.trigger('mouseup', [event]);
            if (this.shouldLeaveMonitor) {
                this.camera.trigger('leftMonitor');
                this.shouldLeaveMonitor = false;
            }
            this.mouseClickInProgress = false;
            this.prevInComputer = this.inComputer;
        }, false);
    }
    /**
     * Creates the iframe for the computer screen
     */
    createIframe() {
        // Create container
        const container = document.createElement('div');
        container.style.width = this.screenSize.width + 'px';
        container.style.height = this.screenSize.height + 'px';
        container.style.opacity = '1';
        container.style.background = '#1d2e2f';
        // Create iframe
        const iframe = document.createElement('iframe');
        // Bubble mouse move events to the main application, so we can affect the camera
        iframe.onload = () => {
            if (iframe.contentWindow) {
                window.addEventListener('message', (event) => {
                    var evt = new CustomEvent(event.data.type, {
                        bubbles: true,
                        cancelable: false,
                    });
                    // @ts-ignore
                    evt.inComputer = true;
                    if (event.data.type === 'mousemove') {
                        var clRect = iframe.getBoundingClientRect();
                        const { top, left, width, height } = clRect;
                        const widthRatio = width / this.iframeSize.width;
                        const heightRatio = height / this.iframeSize.height;
                        // @ts-ignore
                        evt.clientX = Math.round(event.data.clientX * widthRatio + left);
                        //@ts-ignore
                        evt.clientY = Math.round(event.data.clientY * heightRatio + top);
                    }
                    else if (event.data.type === 'keydown') {
                        // @ts-ignore
                        evt.key = event.data.key;
                    }
                    else if (event.data.type === 'keyup') {
                        // @ts-ignore
                        evt.key = event.data.key;
                    }
                    iframe.dispatchEvent(evt);
                });
            }
        };
        // Set iframe attributes
        // PROD
        iframe.src = 'https://hadijmldn.vercel.app/';
        /**
         * Use dev server is query params are present
         *
         * Warning: This will not work unless the dev server is running on localhost:3000
         * Also running the dev server causes browsers to freak out over unsecure connections
         * in the iframe, so it will flag a ton of issues.
         */
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('dev')) {
            iframe.src = 'http://localhost:3000/';
        }
        iframe.style.width = this.screenSize.width + 'px';
        iframe.style.height = this.screenSize.height + 'px';
        iframe.style.padding = IFRAME_PADDING + 'px';
        iframe.style.boxSizing = 'border-box';
        iframe.style.opacity = '1';
        iframe.className = 'jitter';
        iframe.id = 'computer-screen';
        iframe.frameBorder = '0';
        iframe.title = 'HeffernanOS';
        // Add iframe to container
        container.appendChild(iframe);
        // Create CSS plane
        this.createCssPlane(container);
    }
    /**
     * Creates a CSS plane and GL plane to properly occlude the CSS plane
     * @param element the element to create the css plane for
     */
    createCssPlane(element) {
        // Create CSS3D object at the anchor's origin
        const object = new CSS3DObject(element);
        this.cssAnchor.add(object);
        // Create GL plane.
        // NoBlending writes this fragment straight to the framebuffer, so the
        // colour has to be black: the plane's job is to punch a fully
        // transparent hole (0,0,0,0) that the CSS iframe shows through, and any
        // other colour would be written over the iframe as-is.
        const material = new THREE.MeshBasicMaterial({ color: 0x000000 });
        material.side = THREE.DoubleSide;
        material.opacity = 0;
        material.transparent = true;
        material.blending = THREE.NoBlending;
        // Create plane geometry
        const geometry = new THREE.PlaneGeometry(this.screenSize.width, this.screenSize.height);
        // Create the GL plane mesh and sit it on top of the CSS plane
        const mesh = new THREE.Mesh(geometry, material);
        this.anchor.add(mesh);
    }
    /**
     * Creates the texture layers for the computer screen
     * @returns the maximum offset of the texture layers
     */
    createTextureLayers() {
        const textures = this.resources.items.texture;
        this.getVideoTextures('video-1');
        this.getVideoTextures('video-2');
        // Scale factor to multiply depth offset by
        const scaleFactor = 4;
        // Construct the texture layers
        const layers = {
            smudge: {
                texture: textures.monitorSmudgeTexture,
                blending: THREE.AdditiveBlending,
                opacity: 0.12,
                offset: 24,
            },
            innerShadow: {
                texture: textures.monitorShadowTexture,
                blending: THREE.NormalBlending,
                opacity: 1,
                offset: 5,
            },
            video: {
                texture: this.videoTextures['video-1'],
                blending: THREE.AdditiveBlending,
                opacity: 0.5,
                offset: 10,
            },
            video2: {
                texture: this.videoTextures['video-2'],
                blending: THREE.AdditiveBlending,
                opacity: 0.1,
                offset: 15,
            },
        };
        // Declare max offset
        let maxOffset = -1;
        // Add the texture layers to the screen
        for (const [_, layer] of Object.entries(layers)) {
            const offset = layer.offset * scaleFactor;
            this.addTextureLayer(layer.texture, layer.blending, layer.opacity, offset);
            // Calculate the max offset
            if (offset > maxOffset)
                maxOffset = offset;
        }
        // Return the max offset
        return maxOffset;
    }
    getVideoTextures(videoId) {
        const video = document.getElementById(videoId);
        if (!video) {
            setTimeout(() => {
                this.getVideoTextures(videoId);
            }, 100);
        }
        else {
            this.videoTextures[videoId] = new THREE.VideoTexture(video);
        }
    }
    /**
     * Adds a texture layer to the screen
     * @param texture the texture to add
     * @param blending the blending mode
     * @param opacity the opacity of the texture
     * @param offset the offset of the texture, higher values are further from the screen
     */
    addTextureLayer(texture, blendingMode, opacity, offset) {
        // Create material
        const material = new THREE.MeshBasicMaterial({
            map: texture,
            blending: blendingMode,
            side: THREE.DoubleSide,
            opacity,
            transparent: true,
        });
        // Create geometry
        const geometry = new THREE.PlaneGeometry(this.screenSize.width, this.screenSize.height);
        // Create mesh, offset along the screen's own outward normal
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.z = offset;
        this.anchor.add(mesh);
    }
    /**
     * Creates enclosing planes for the computer screen
     * @param maxOffset the maximum offset of the texture layers
     */
    createEnclosingPlanes(maxOffset) {
        // Create planes, lots of boiler plate code here because I'm lazy
        const planes = {
            left: {
                size: new THREE.Vector2(maxOffset, this.screenSize.height),
                position: new THREE.Vector3(-this.screenSize.width / 2, 0, maxOffset / 2),
                rotation: new THREE.Euler(0, 90 * THREE.MathUtils.DEG2RAD, 0),
            },
            right: {
                size: new THREE.Vector2(maxOffset, this.screenSize.height),
                position: new THREE.Vector3(this.screenSize.width / 2, 0, maxOffset / 2),
                rotation: new THREE.Euler(0, 90 * THREE.MathUtils.DEG2RAD, 0),
            },
            top: {
                size: new THREE.Vector2(this.screenSize.width, maxOffset),
                position: new THREE.Vector3(0, this.screenSize.height / 2, maxOffset / 2),
                rotation: new THREE.Euler(90 * THREE.MathUtils.DEG2RAD, 0, 0),
            },
            bottom: {
                size: new THREE.Vector2(this.screenSize.width, maxOffset),
                position: new THREE.Vector3(0, -this.screenSize.height / 2, maxOffset / 2),
                rotation: new THREE.Euler(90 * THREE.MathUtils.DEG2RAD, 0, 0),
            },
        };
        // Add each of the planes
        for (const [_, plane] of Object.entries(planes)) {
            this.createEnclosingPlane(plane);
        }
    }
    /**
     * Creates a plane for the enclosing planes
     * @param plane the plane to create
     */
    createEnclosingPlane(plane) {
        const material = new THREE.MeshBasicMaterial({
            side: THREE.DoubleSide,
            color: 0x48493f,
        });
        const geometry = new THREE.PlaneGeometry(plane.size.x, plane.size.y);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.copy(plane.position);
        mesh.rotation.copy(plane.rotation);
        this.anchor.add(mesh);
    }
    /**
     * A black plane that adds alpha without adding colour. Raising its opacity
     * makes the WebGL layer more opaque over the iframe behind it, which reads
     * as the screen going dark — so the website is legible when you are looking
     * straight at it and fades to a glow from across the room.
     */
    createPerspectiveDimmer(maxOffset) {
        const material = new THREE.MeshBasicMaterial({
            side: THREE.DoubleSide,
            color: 0x000000,
            transparent: true,
            blending: THREE.AdditiveBlending,
        });
        const plane = new THREE.PlaneGeometry(this.screenSize.width, this.screenSize.height);
        const mesh = new THREE.Mesh(plane, material);
        mesh.position.z = maxOffset - 5;
        this.dimmingPlane = mesh;
        this.anchor.add(mesh);
    }
    update() {
        if (!this.dimmingPlane)
            return;
        const viewVector = new THREE.Vector3()
            .copy(this.camera.instance.position)
            .sub(this.position)
            .normalize();
        // How square-on the camera is to the screen: 1 head on, 0 edge on.
        // Distance deliberately plays no part — the site stays legible from
        // anywhere in the room, and only fades as the screen turns away.
        const facing = Math.max(viewVector.dot(this.normal), 0);
        // Must stay inside 0..1. The opacity lands in the alpha channel of a
        // float render target during post processing, where an out of range
        // value is not clamped for us and corrupts the iframe cutout.
        const material = this.dimmingPlane.material;
        material.opacity = THREE.MathUtils.clamp((1 - facing) * DIM_FACTOR, 0, 1);
    }
}
